import { ClipMoreMenu } from "@components/ClipMoreMenu/ClipMoreMenu";
import { IconLineTrash } from "@kalodata/kds-design-icons";
import type { UserAssetRecord } from "@apis/http/api";
import classNames from "classnames";
import { useState, type SyntheticEvent } from "react";
import { t } from "i18next";

type Composition = "portrait" | "landscape";

interface UIImageCardProps {
  record: UserAssetRecord;
  onPreview?: (src: string) => void;
  onDelete?: (record: UserAssetRecord) => void;
  className?: string;
}

const UIImageCard = ({ record, onPreview, onDelete, className }: UIImageCardProps) => {
  const src = record.url || "";
  const [composition, setComposition] = useState<Composition>("portrait");
  const canPreview = !!src;

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (!naturalWidth || !naturalHeight) return;
    setComposition(naturalWidth > naturalHeight ? "landscape" : "portrait");
  };

  const handleClick = () => {
    if (!canPreview) return;
    onPreview?.(src);
  };

  return (
    <ClipMoreMenu
      className={classNames(
        "flex aspect-square w-full items-center justify-center overflow-hidden rounded-[8px] bg-kds-dark",
        className,
      )}
      options={[
        {
          key: "delete",
          label: t("Delete"),
          icon: <IconLineTrash className="text-kds-fg" />,
          onClick: () => onDelete?.(record),
        },
      ]}
    >
      <button
        type="button"
        disabled={!canPreview}
        className={classNames(
          "flex h-full w-full items-center justify-center border-0 bg-transparent p-0",
          canPreview ? "cursor-pointer" : "cursor-default",
        )}
        onClick={handleClick}
      >
        {src ? (
          <img
            src={src}
            alt=""
            className={classNames(
              "pointer-events-none h-full w-full",
              composition === "portrait" ? "object-cover" : "object-contain",
            )}
            onLoad={handleImageLoad}
          />
        ) : (
          <div className="h-full w-full bg-kds-bg-subtle" />
        )}
      </button>
    </ClipMoreMenu>
  );
};

export default UIImageCard;
